import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
  structuredData?: object;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = 'https://futuremindsacademy.net/og-image.jpg',
  noindex = false,
  structuredData
}) => {
  const siteUrl = 'https://futuremindsacademy.net';
  const isPagesDevHost = window.location.hostname === 'futureminds-academy.pages.dev';
  const fullTitle = title.includes('Future Minds') ? title : `${title} | Future Minds Academy`;
  const canonicalUrl = canonical ? `${siteUrl}${canonical}` : undefined;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    // Update meta keywords
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords);
    }

    // Update robots meta
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute(
        'content',
        noindex || isPagesDevHost ? 'noindex, nofollow' : 'index, follow'
      );
    }

    // Update canonical link
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (linkCanonical && canonicalUrl) {
      linkCanonical.href = canonicalUrl;
    }

    // Update Open Graph tags
    const updateOG = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (meta) {
        meta.setAttribute('content', content);
      }
    };

    updateOG('og:title', fullTitle);
    updateOG('og:description', description);
    updateOG('og:type', ogType);
    updateOG('og:image', ogImage);
    if (canonicalUrl) {
      updateOG('og:url', canonicalUrl);
    }

    // Update Twitter tags
    const updateTwitter = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (meta) {
        meta.setAttribute('content', content);
      }
    };

    updateTwitter('twitter:title', fullTitle);
    updateTwitter('twitter:description', description);
    updateTwitter('twitter:image', ogImage);

    // Add structured data if provided
    if (structuredData) {
      const existingScript = document.querySelector('script[data-seo="page-schema"]');
      if (existingScript) {
        existingScript.remove();
      }
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'page-schema');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);

      return () => {
        script.remove();
      };
    }
  }, [fullTitle, description, keywords, canonicalUrl, ogType, ogImage, noindex, structuredData, isPagesDevHost]);

  return null;
};

export default SEO;
